import React, { useState } from 'react';
import { Button, Input, message, Tooltip } from 'antd';
import { ApiOutlined, AudioOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { extractOrderEntities } from '../../utils/aiAssistant';
import { transportBodyTypes } from '../search/categories'; // fixed path
// Assuming we have transportationType values, let's map text to them if possible.

// Helper to geocode a string to coordinates + country code
const geocodeAddress = (address, direction, form, setLatLng) => {
    return new Promise((resolve) => {
        if (!address) return resolve(false);

        if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
            console.warn('Google Maps API not fully loaded yet.');
            return resolve(false);
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
                const selectedPlace = results[0];
                const lat = selectedPlace.geometry.location.lat();
                const lng = selectedPlace.geometry.location.lng();

                if (selectedPlace.address_components) {
                    const countryComponent = selectedPlace.address_components.find(
                        (component) => component.types.includes('country')
                    );
                    if (countryComponent) {
                        form.setFieldsValue({
                            [`\${direction}_country_code`]: countryComponent.short_name,
                        });
                        form.setFieldsValue({
                            [`\${direction}_country`]: countryComponent.long_name,
                        });
                    }
                }

                // Use generic direction mapping mapped to origin/destination fields
                const fieldMap = direction === 'from' ? 'from_address' : 'to_address';
                form.setFieldsValue({ [fieldMap]: selectedPlace.formatted_address });

                setLatLng((prev) => ({
                    ...prev,
                    [direction]: { lat, lng },
                }));

                resolve(true);
            } else {
                console.error('Geocode failed for', address, ':', status);
                resolve(false);
            }
        });
    });
};

const MobileAssistantIcon = () => (
    <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M20.8787 4.32594C22.0503 5.49752 22.0503 7.39701 20.8787 8.56859L10.1498 19.2975L4.22205 20.8918L5.81641 14.9641L16.5453 4.23521C17.7169 3.06364 19.6164 3.06364 20.7879 4.23521L20.8787 4.32594Z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
        <rect x="15.2334" y="6.12801" width="5.12831" height="1" transform="rotate(45 15.2334 6.12801)" stroke="white" />
        <path d="M5.34053 1C6.02943 3.03955 7.64266 4.65232 9.68178 5.3416C7.64296 6.03075 6.03053 7.64288 5.34124 9.68178C4.65169 7.64324 3.03905 6.03061 1 5.3416C3.03881 4.65236 4.65139 3.03957 5.34053 1Z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

const MobileMicIcon = () => (
    <svg width="14" height="21" viewBox="0 0 14 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M13 7C13.5523 7 14 7.44772 14 8V10C14 13.5264 11.3921 16.4414 8 16.9268V19H11C11.5523 19 12 19.4477 12 20C12 20.5523 11.5523 21 11 21H3C2.44772 21 2 20.5523 2 20C2 19.4477 2.44772 19 3 19H6V16.9268C2.60787 16.4414 0 13.5264 0 10V8C0 7.44772 0.447715 7 1 7C1.55228 7 2 7.44772 2 8V10C2 12.7614 4.23858 15 7 15C9.76142 15 12 12.7614 12 10V8C12 7.44772 12.4477 7 13 7ZM7 0C9.20914 0 11 1.79086 11 4V10C11 12.2091 9.20914 14 7 14C4.79086 14 3 12.2091 3 10V4C3 1.79086 4.79086 0 7 0Z" fill="white" />
    </svg>
);

const AiAutofill = ({ form, setLatLng, setSelectedCategory, clearForm, shouldClearBeforeAutofill = true }) => {
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState('');
    const [listening, setListening] = useState(false);
    const recognitionRef = React.useRef(null);
    const textRef = React.useRef(text);
    const handleAutofillRef = React.useRef(null);
    const timerRef = React.useRef(null);

    React.useEffect(() => {
        textRef.current = text;
    }, [text]);

    React.useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = 'ru-RU';

            recognitionRef.current.onresult = (event) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }
                if (finalTranscript) {
                    setText((prev) => {
                        const nextText = prev + (prev ? ' ' : '') + finalTranscript;
                        textRef.current = nextText;
                        return nextText;
                    });
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error', event.error);
                if (event.error !== 'no-speech') {
                    message.error('Ошибка распознавания речи: ' + event.error);
                }
                setListening(false);
                if (timerRef.current) clearTimeout(timerRef.current);
            };

            recognitionRef.current.onend = () => {
                setListening(false);
                if (timerRef.current) clearTimeout(timerRef.current);
                if (textRef.current && textRef.current.trim() && handleAutofillRef.current) {
                    handleAutofillRef.current(textRef.current);
                }
            }
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const startListening = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!recognitionRef.current) {
            message.warning('Ваш браузер не поддерживает голосовой ввод');
            return;
        }
        if (!listening) {
            try {
                recognitionRef.current.start();
                setListening(true);
                timerRef.current = setTimeout(() => {
                    stopListening();
                    message.info('Запись остановлена (лимит 1 минута)');
                }, 60000);
            } catch (error) {
                console.error(error);
            }
        }
    };

    const stopListening = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (listening && recognitionRef.current) {
            recognitionRef.current.stop();
            setListening(false);
            if (timerRef.current) clearTimeout(timerRef.current);
        }
    };

    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 992;

    const handleDictateClick = (e) => {
        if (isMobile) return;
        if (listening) {
            stopListening(e);
        } else {
            startListening(e);
        }
    };

    const handleAutofill = async (customText) => {
        const textToProcess = typeof customText === 'string' ? customText : text;

        if (!textToProcess.trim()) {
            message.warning('Пожалуйста, введите текст для анализа.');
            return;
        }

        if (shouldClearBeforeAutofill) {
            if (clearForm) {
                clearForm();
            } else {
                form.resetFields();
                setLatLng({ from: {}, to: {} });
                setSelectedCategory(null);
            }
        }

        setLoading(true);
        try {
            const data = await extractOrderEntities(textToProcess);
            console.log('Extracted Data:', data);

            if (!data) {
                message.error('Сбой искусственного интеллекта. Пожалуйста, попробуйте еще раз.');
                setLoading(false);
                return;
            }

            // Geocode and Set Locations
            if (data.fromLocation) {
                await geocodeAddress(data.fromLocation, 'from', form, setLatLng);
            }
            if (data.toLocation) {
                await geocodeAddress(data.toLocation, 'to', form, setLatLng);
            }

            // Prepare updates for the form
            const formUpdates = {};

            // Fallback for antd date objects
            if (data.fromDate && data.toDate) {
                formUpdates.dates = [dayjs(data.fromDate), dayjs(data.toDate)];
            } else if (data.fromDate) {
                formUpdates.dates = [dayjs(data.fromDate), dayjs(data.fromDate).add(1, 'day')];
            } else if (data.toDate) {
                formUpdates.dates = [dayjs(data.toDate).subtract(1, 'day'), dayjs(data.toDate)];
            }

            // Direct matching mappings
            const directFields = [
                'weight', 'volume', 'length', 'width', 'height',
                'type', 'package_type', 'load_type',
                'temperature_min', 'temperature_max',
                'cargo_price', 'price', 'currency',
                'payment_method', 'payment_form', 'nds', 'comment',
                'count_of_transport'
            ];

            directFields.forEach(field => {
                if (data[field] !== null && data[field] !== undefined) {
                    if (field === 'type') {
                        formUpdates[field] = data[field].toString();
                    } else if (field === 'load_type' && Array.isArray(data[field])) {
                        formUpdates[field] = data[field].map(String);
                    } else {
                        formUpdates[field] = data[field];
                    }
                }
            });

            // Map Pallets to Count (if applicable, as requested by the payload structure)
            if (data.pallets !== null && data.pallets !== undefined) {
                formUpdates.count = data.pallets;
            }

            // Handle temperature boolean dependency
            if (data.temperature || (data.temperature_min !== null && data.temperature_min !== undefined) || (data.temperature_max !== null && data.temperature_max !== undefined)) {
                formUpdates.temperature = true;
            }

            // Handle cargo_price dependencies
            if (data.cargo_price !== null && data.cargo_price !== undefined) {
                formUpdates.cargo_price_require = true;
                formUpdates.cargo_price_currency = data.currency || 1;
            }

            // Transport Type mapping (1=FTL, 2=LTL/Сборный, etc.)
            if (data.transportType) {
                if (data.transportType === 'FTL') {
                    formUpdates.type_of_transportation = 1;
                } else if (data.transportType === 'LTL') {
                    formUpdates.type_of_transportation = 2;
                }
            }

            // Body Type Mapping
            if (data.bodyType) {
                const matchedBodyType = transportBodyTypes.find(b =>
                    b.name.toLowerCase() === data.bodyType.toLowerCase() ||
                    data.bodyType.toLowerCase().includes(b.name.toLowerCase())
                );
                if (matchedBodyType) {
                    formUpdates.transport_body_type = matchedBodyType.id;

                    let targetCategory = null;
                    if (matchedBodyType.category && matchedBodyType.category.length > 0) {
                        targetCategory = matchedBodyType.category[0];
                    } else if (matchedBodyType.parent_id !== null) {
                        const parentBody = transportBodyTypes.find(b => b.id === matchedBodyType.parent_id);
                        if (parentBody && parentBody.category && parentBody.category.length > 0) {
                            targetCategory = parentBody.category[0];
                        }
                    }

                    if (targetCategory) {
                        formUpdates.transport_category = targetCategory;
                        if (setSelectedCategory) {
                            setSelectedCategory(targetCategory);
                        }
                    }
                }
            }

            // Apply the fields
            if (Object.keys(formUpdates).length > 0) {
                if (formUpdates.transport_body_type) {
                    const { transport_body_type, ...restUpdates } = formUpdates;
                    form.setFieldsValue(restUpdates);
                    setTimeout(() => {
                        form.setFieldsValue({ transport_body_type });
                    }, 50);
                } else {
                    form.setFieldsValue(formUpdates);
                }
                message.success('Форма успешно заполнена искусственным интеллектом!');
            }

        } catch (error) {
            console.error(error);
            message.error('Ошибка анализа текста. Возможно, отсутствует ключ API или проблема с подключением.');
        } finally {
            setLoading(false);
        }
    };
    handleAutofillRef.current = handleAutofill;

    const handleMobileAutofill = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (loading || listening) return;
        handleAutofill();
    };

    if (isMobile) {
        return (
            <div className={`ai-autofill-mobile ${listening ? 'is-listening' : ''} ${loading ? 'is-loading' : ''}`}>
                <div className="ai-autofill-mobile__field">
                    <button
                        type="button"
                        className="ai-autofill-mobile__magic"
                        onClick={handleMobileAutofill}
                        disabled={loading || listening}
                        aria-label="Умное заполнение"
                    >
                        {loading ? <LoadingOutlined spin /> : <MobileAssistantIcon />}
                    </button>
                    <Input
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onPressEnter={handleMobileAutofill}
                        placeholder={listening ? 'Говорите...' : 'Умное заполнение'}
                        disabled={loading}
                        className="ai-autofill-mobile__input"
                        bordered={false}
                    />
                </div>
                <Button
                    htmlType="button"
                    className="ai-autofill-mobile__mic"
                    shape="circle"
                    icon={<MobileMicIcon />}
                    disabled={loading}
                    onPointerDown={startListening}
                    onPointerUp={stopListening}
                    onPointerCancel={stopListening}
                    onPointerLeave={stopListening}
                    onContextMenu={(e) => e.preventDefault()}
                    aria-label={listening ? 'Остановить запись' : 'Начать запись'}
                />
            </div>
        );
    }

    return (
        <div className="ai-autofill-container" style={{ marginBottom: 24, padding: 16, background: '#f0f5ff', borderRadius: 8, border: '1px solid #d6e4ff' }}>
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ApiOutlined style={{ color: '#1677ff', fontSize: 18 }} />
                    <span style={{ fontWeight: '500', color: '#1677ff', fontSize: 16 }}>Умное автозаполнение</span>
                </div>
            </div>
            <Input.TextArea
                placeholder="Вставьте текст от клиента (например: Нужно перевезти 10 паллет электроники из Нью-Йорка в Лос-Анджелес 15-го числа...)"
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                style={{ marginBottom: 12 }}
                disabled={loading}
            />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                    type="primary"
                    icon={<ApiOutlined />}
                    onClick={handleAutofill}
                    loading={loading || listening}
                    style={{ background: '#1677ff' }}
                >
                    Заполнить форму
                </Button>
                <Tooltip title={isMobile ? "Удерживайте для записи" : "Нажмите для записи"}>
                    <Button
                        type={listening ? "primary" : "default"}
                        danger={listening}
                        icon={<AudioOutlined />}
                        onMouseDown={isMobile ? startListening : undefined}
                        onMouseUp={isMobile ? stopListening : undefined}
                        onMouseLeave={isMobile ? stopListening : undefined}
                        onTouchStart={isMobile ? startListening : undefined}
                        onTouchEnd={isMobile ? stopListening : undefined}
                        onClick={!isMobile ? handleDictateClick : undefined}
                        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                    >
                        {listening ? "Идет запись..." : "Диктовать"}
                    </Button>
                </Tooltip>
                <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    {isMobile
                        ? '*Удерживайте кнопку "Диктовать" для голосового ввода (макс. 1 мин)'
                        : '*Нажмите кнопку "Диктовать" для старта/остановки ввода (макс. 1 мин)'}
                </span>
            </div>
        </div>
    );
};

export default AiAutofill;
